import { Dialog, Typography } from '@material-ui/core';
import { Box, Button } from '@superdispatch/ui-lab';
import styled from 'styled-components';

import { ColorDynamic, Inline, Stack } from '@superdispatch/ui';
import { useEffect, useState } from 'react';
import { trackEvent } from 'shared/helpers/Analytics';
import {
  useCarrierSettings,
  useCarrierSettingsCache,
} from 'shared/settings/CarrierSettingsAPI';
import { useInstantTransferInvitation } from '../SuperPayAPI';
import instantTransferIllustration from './assets/instant_transfer_invitation.png';
import { InstantTransferDialog } from './InstantTransferDialog';
import { Source } from 'shared/modules/superpay/instant-transfer/InstantTransferDialogContent';

const List = styled.ul`
  padding-inline-start: 20px;
`;

const IllustrationImage = styled.img`
  margin-bottom: 16px;
  width: 473px;
`;

const Body = styled.div`
  z-index: 1;
  flex-direction: column;
  background-color: ${ColorDynamic.White};
  padding: 16px;
  position: relative;
  display: flex;
  margin-top: 8px;
  margin-bottom: 8px;
  width: 100%;
`;

interface InstantTransferDialogContentProps {
  onClose: () => void;
  onInstantTransferClick: () => void;
}

function InstantTransferInvitationContent({
  onClose,
  onInstantTransferClick,
}: InstantTransferDialogContentProps) {
  return (
    <Box width="505px">
      <Body>
        <IllustrationImage
          src={instantTransferIllustration}
          alt="Instant Transfer Illustration"
        />
        <Stack space="xsmall">
          <Typography variant="h2">Instant Transfer is here.</Typography>
          <Typography variant="h4">
            Your money&apos;s on the way—skip the wait and get paid directly
            from Super Dispatch in seconds.
          </Typography>
        </Stack>

        <List>
          <li>
            <Typography>Instant payouts on eligible loads</Typography>
          </li>
          <li>
            <Typography>Works even on weekends and holidays</Typography>
          </li>
        </List>

        <Stack space="xsmall">
          <Typography>
            You&apos;re one of a few trusted carriers invited to try it first.
          </Typography>
          <Typography>
            If you experience any issues, please contact Super Dispatch Support.
          </Typography>
        </Stack>
        <Box marginTop="small" marginBottom="small">
          <Inline space="small" horizontalAlign="center">
            <Button
              variant="primary"
              onClick={() => {
                onInstantTransferClick();
                trackEvent(
                  'Carrier Clicked See Instant Transfer Offer on Instant Transfer Invitation Pop Up',
                );
              }}
            >
              See Instant Transfer Offer
            </Button>

            <Button
              variant="neutral"
              onClick={() => {
                onClose();
                trackEvent(
                  'Carrier Clicked Got it on Instant Transfer Invitation Pop Up',
                );
              }}
            >
              Got it
            </Button>
          </Inline>
        </Box>
      </Body>
    </Box>
  );
}

interface InstantTransferInvitationBannerProps {
  source?: Source;
}

export function InstantTransferInvitationBanner({
  source,
}: InstantTransferInvitationBannerProps) {
  const [dialog, setDialog] = useState<'invitation' | 'transfer' | null>(null);
  const { data: settings } = useCarrierSettings();
  const { invalidateCarrierSettings } = useCarrierSettingsCache();
  const loadGud =
    settings?.superpay.instant_transfer_invitation_load_guid || '';
  const { mutateAsync: markInvitationSeen } = useInstantTransferInvitation();

  const handleSeeOffer = () => {
    void markInvitationSeen({});
    setDialog('transfer');
  };

  const handleClose = () => {
    void markInvitationSeen({});
    invalidateCarrierSettings();
    setDialog(null);
  };

  useEffect(() => {
    if (loadGud) {
      setDialog('invitation');
    }
  }, [loadGud]);

  return (
    <>
      <Dialog
        open={dialog === 'invitation'}
        onClose={handleClose}
        maxWidth={false}
      >
        <InstantTransferInvitationContent
          onClose={handleClose}
          onInstantTransferClick={handleSeeOffer}
        />
      </Dialog>

      <InstantTransferDialog
        open={dialog === 'transfer'}
        onClose={handleClose}
        loadGUID={loadGud}
        source={source}
      />
    </>
  );
}
